import React from 'react';
import { formatTime } from '../../../../services/locationService';
import { getLocationName } from '../../../../util/locationUtils';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// Compact hours summary: groups contiguous days with the same open/close into ranges.
// e.g. [{open, 8:00-17:00} x 5, closed x 2]  →  "Mon–Fri 8:00 AM - 5:00 PM · Sat–Sun Closed"
const formatHoursSummary = (hours) => {
  if (!hours || hours.length === 0) return 'Not configured';

  const byDay = new Array(7).fill(null);
  hours.forEach(h => {
    const d = typeof h.day_of_week === 'number' ? h.day_of_week : DAY_NAMES.indexOf(h.day_name);
    if (d >= 0 && d < 7) byDay[d] = h;
  });

  // Build sig per day for grouping; assume week starts Monday for the summary.
  const order = [1, 2, 3, 4, 5, 6, 0];
  const sig = (h) => (!h || !h.is_open) ? 'closed' : `${h.open_time}|${h.close_time}`;
  const label = (h) => (!h || !h.is_open) ? 'Closed' : `${formatTime(h.open_time)} - ${formatTime(h.close_time)}`;
  const short = (d) => DAY_NAMES[d].slice(0, 3);

  const groups = [];
  let i = 0;
  while (i < order.length) {
    const start = order[i];
    const s = sig(byDay[start]);
    let j = i;
    while (j + 1 < order.length && sig(byDay[order[j + 1]]) === s) j++;
    const end = order[j];
    const range = start === end ? short(start) : `${short(start)}–${short(end)}`;
    groups.push(`${range} ${label(byDay[start])}`);
    i = j + 1;
  }
  return groups.join(' · ');
};

const LocationsTab = ({
  mode,
  locations,
  locationData,
  location,
  locData,
  canAddLocation,
  onAddLocation,
  onOpenOperatingHoursModal,
  onOpenHolidaysModal,
  onOpenCustomClosuresModal,
  renderLocationAddress
}) => {
  // ── Installer mode: list every location, allow Add ──────────────────
  if (mode === 'installer') {
    return (
      <div className="card mb-4">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h4 className="mb-0">
            <i className="fa-sharp fa-regular fa-map-marker-alt mr-2"></i>
            Locations
          </h4>
          {canAddLocation && (
            <button className="btn btn-outline-primary btn-sm" onClick={onAddLocation}>
              <i className="fa-sharp fa-regular fa-plus mr-1"></i> Add Location
            </button>
          )}
        </div>
        <div className="card-body">
          {(!locations || locations.length === 0) ? (
            <p className="text-muted mb-0">No locations set up yet. Add one to start configuring hours and holidays.</p>
          ) : (
            <p className="text-muted small mb-3">
              To edit a location's address, hours, or closures, select it from the location dropdown in the page header.
            </p>
          )}
          {/* Plain bordered rows — avoid .list-group because a global rule in
              requestimates.css forces every list-group in the app to 200px fixed-position. */}
          {locations && locations.length > 0 && (
            <div>
              {locations.map((loc, idx) => {
                const ld = locationData?.[loc.location_id];
                const hoursSummary = formatHoursSummary(ld?.operatingHours);
                return (
                  <div
                    key={loc.location_id}
                    className={`py-3 ${idx > 0 ? 'border-top' : ''}`}
                  >
                    <div className="row">
                      <div className="col-md-8">
                        <h6 className="mb-1">{getLocationName(loc)}</h6>
                        <div className="text-muted small">{renderLocationAddress(loc)}</div>
                      </div>
                      <div className="col-md-4 text-md-right text-muted small mt-2 mt-md-0">
                        <i className="fa-sharp fa-regular fa-clock mr-1"></i>
                        {hoursSummary}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Location / merged mode: single-location editor ──────────────────
  if (!location) {
    return <p className="text-muted">No location selected.</p>;
  }

  const hours = locData?.operatingHours || [];
  const locationId = location.location_id;

  // Single-location installers can still add another location from here — that's
  // the only entry point into multi-location for them. Multi-loc accounts use the
  // installer-mode list view instead.
  const showAddButtonHere = mode === 'merged' && canAddLocation;

  return (
    <>
      {/* Address */}
      <div className="card mb-4">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h4 className="mb-0">
            <i className="fa-sharp fa-regular fa-map-marker-alt mr-2"></i>
            Address
          </h4>
          {showAddButtonHere && (
            <button className="btn btn-outline-primary btn-sm" onClick={onAddLocation}>
              <i className="fa-sharp fa-regular fa-plus mr-1"></i> Add Location
            </button>
          )}
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6">
              {renderLocationAddress(location)}
            </div>
            <div className="col-md-6">
              {/* Google Reviews */}
              {location.google_rating && (
                <div>
                  <strong>Google Reviews</strong>
                  <div className="mt-1">
                    <span style={{ color: '#f4b400', fontSize: '1.1rem', letterSpacing: '1px' }}>
                      {'★'.repeat(Math.floor(location.google_rating))}
                      {location.google_rating - Math.floor(location.google_rating) >= 0.3 ? '½' : ''}
                    </span>
                    <span className="ml-2 font-weight-bold">{location.google_rating}</span>
                    {location.google_review_count && (
                      <span className="text-muted ml-1">({location.google_review_count} reviews)</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Operating Hours */}
      <div className="card mb-4">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h4 className="mb-0">
            <i className="fa-sharp fa-regular fa-clock mr-2"></i>
            Operating Hours
          </h4>
          <div>
            <button
              className="btn btn-outline-primary btn-sm mr-2"
              onClick={() => onOpenOperatingHoursModal(locationId)}
            >
              <i className="fa-sharp fa-regular fa-edit mr-1"></i> Edit
            </button>
            <button
              className="btn btn-outline-secondary btn-sm mr-2"
              onClick={() => onOpenHolidaysModal(locationId)}
            >
              <i className="fa-sharp fa-regular fa-calendar-star mr-1"></i> Holidays
            </button>
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => onOpenCustomClosuresModal(locationId)}
            >
              <i className="fa-sharp fa-regular fa-calendar-xmark mr-1"></i> Closures
            </button>
          </div>
        </div>
        <div className="card-body">
          {hours.length > 0 ? (
            <div className="operating-hours-grid">
              {hours.map((day, index) => (
                <div key={index} className="d-flex justify-content-between align-items-center py-2 border-bottom">
                  <span className="font-weight-bold" style={{ width: '100px' }}>
                    {day.day_name || DAY_NAMES[day.day_of_week]}
                  </span>
                  <span className={day.is_open ? 'text-success' : 'text-muted'}>
                    {day.is_open
                      ? `${formatTime(day.open_time)} - ${formatTime(day.close_time)}`
                      : 'Closed'}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <span className="text-muted">No hours configured</span>
          )}
        </div>
      </div>
    </>
  );
};

export default LocationsTab;
